import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AppConfig } from '@app/core/app.config';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { RelatedAsset } from '@app/core/model/entities/asset/asset';
import { CheckInput } from '@app/core/model/entities/asset/check';
import { CheckType } from '@app/core/model/entities/asset/check-type';
import { Space } from '@app/core/model/entities/asset/space';
import { SECTION_ENTITY_TYPE_INJECTION } from '@app/core/model/other/field-config';
import { AssetsService } from '@app/features/main/views/assets/assets.service';
import { SpacesService } from '@app/features/main/views/organization-spaces/spaces.service';
import { eq } from '@app/shared/extra/filter-model-builder';
import { getLocale } from '@app/shared/extra/utils';
import { ColumnBuilder } from '@app/shared/grid/column-builder';
import { SelectParams } from '@app/shared/grid/datasource-utils';
import { GridOptionsService } from '@app/shared/grid/grid-options.service';
import { CheckTypeService } from '@app/shared/services/check-type.service';
import { ExtraValidators } from '@app/shared/validators/extra-validators.module';
import { TranslateService } from '@ngx-translate/core';
import { AccessManager } from '@services/managers/access.manager';
import { GridApi, GridOptions, RowSelectedEvent } from 'ag-grid-community';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Component({
  templateUrl: './organization-check-create-modal.component.html',
  styleUrls: ['./organization-check-create-modal.component.scss'],
  standalone: false,
  providers: [
    {provide: SECTION_ENTITY_TYPE_INJECTION, useValue: EntityTypeEnum.CHECK}
  ]
})
export class OrganizationCheckCreateModalComponent implements OnInit, OnDestroy {

  protected readonly EntityTypeEnum = EntityTypeEnum;

  public checkTypeForm: FormGroup<{ checkType: FormControl<string> }>;
  public checkSpaceForm: FormGroup<{
    asset: FormControl<RelatedAsset>,
    spaces: FormControl<Space[]>
  }>;

  public gridApi: GridApi;
  public gridOptions: GridOptions;

  public checkTypes: Observable<CheckType[]>;
  public fileIconPath = 'assets/images/icon/file.png';

  public spaces = new BehaviorSubject<Space[]>([]);

  protected assetTypeAllowsSpaceModule = false;
  protected missingRequiredFields: { code: string, label: string }[];

  private invalidRows: string[] = [];

  public appConfig = inject(AppConfig);
  protected fb = inject(FormBuilder);
  protected assetsService = inject(AssetsService);
  protected spacesService = inject(SpacesService);
  protected columnBuilder = inject(ColumnBuilder);
  protected translate: TranslateService = inject(TranslateService);
  protected checkTypeService = inject(CheckTypeService);
  protected gridOptionsService = inject(GridOptionsService);
  private accessManager = inject(AccessManager);
  protected destroyRef = inject(DestroyRef);

  constructor() {
    this.gridOptions = {
      ...this.gridOptionsService.staticSelectSpacesGridOptions,
      defaultColDef: {
        resizable: true,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        filter: true
      },
      treeData: true,
      localeText: this.columnBuilder.localeTextCommon(),
      rowClassRules: {
        'invalid-row': ({data}: { data: Space }): boolean => this.invalidRows.includes(data.id)
      },
      onRowClicked: this.onRowSelected.bind(this),
      onRowSelected: this.onRowSelected.bind(this)
    };

    // Init Check type form
    this.checkTypeForm = this.fb.group({
      checkType: this.fb.control('', Validators.required)
    });

    // Init Check space form
    this.checkSpaceForm = this.fb.group({
      asset: this.fb.control(null, Validators.compose([
        Validators.required
      ])),
      spaces: this.fb.control([], Validators.compose([
        ExtraValidators.checkSpaces
      ]))
    });

    this.missingRequiredFields = [
      {code: 'type', label: this.translate.instant('LABEL.CHECK_TYPE')},
      {code: 'assetId', label: this.translate.instant('LABEL.ASSET')},
      {code: 'spaceIds', label: this.translate.instant('LABEL.SPACES')},
    ].filter(({code}) => !this.accessManager.hasFieldWriteAccess(code, EntityTypeEnum.CHECK));
  }

  /**
   * Fetch Check types, Assets and Spaces.
   */
  public ngOnInit(): void {
    // Fetch Check types
    this.checkTypes = this.checkTypeService.checkTypes$
      .pipe(
        map(checkTypes => Object.values(checkTypes)),
        takeUntilDestroyed(this.destroyRef)
      );

    // Listen for Spaces to be displayed in the grid
    this.checkSpaceForm.controls.asset.valueChanges
      .pipe(
        switchMap((relatedAsset?: RelatedAsset) => {
          this.assetTypeAllowsSpaceModule = relatedAsset?.typeModules.includes(EntityTypeEnum.SPACE);
          if (this.assetTypeAllowsSpaceModule) {
            this.checkSpaceForm.controls.spaces.addValidators(Validators.required);
            const selectParams: SelectParams = {
              locale: getLocale(),
              filters: [
                eq('assetId', relatedAsset.id)
              ]
            };
            return this.spacesService.loadAllSpaces(selectParams);
          }
          this.checkSpaceForm.controls.spaces.removeValidators(Validators.required);
          return of([]);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(this.spaces);
  }

  /**
   * Stop Observable subscriptions.
   */
  public ngOnDestroy(): void {
    this.spaces.complete();
  }

  /**
   * Load the grid's column definitions and fill with the selected Asset's Spaces.
   * @param params Ag-grid params.
   */
  public onGridReady(params): void {
    this.gridApi = params.api;
    this.gridApi.applyColumnState({
      state: [
        {colId: 'ag-Grid-AutoColumn', sort: 'asc'}
      ]
    });

    // Resize columns when receiving new Spaces
    this.spaces.subscribe(spaces => {
      this.gridApi.setGridOption('rowData', spaces);
      this.gridApi.sizeColumnsToFit();
      this.checkSpaceForm.controls.spaces.reset([]);
    });
  }

  public getGeneratedObject(): CheckInput {
    return {
      assetId: this.checkSpaceForm.controls.asset.value?.id,
      type: this.checkTypeForm.controls.checkType.value,
      spaceIds: this.checkSpaceForm.controls.spaces.value.map(space => space.id)
    };
  }

  /**
   * Set the form value for checkType after the User clicks on a Check type.
   * @param checkType Check type value to use.
   */
  public selectType(checkType: CheckType): void {
    // Register type of selected check
    this.checkTypeForm.setValue({'checkType': checkType.code});
  }

  /**
   * Get the number of columns to display depending on the viewport's width.
   * @return Number of columns.
   */
  public getColCount(): number {
    return matchMedia('(max-width: 550px)').matches ? 1 : 2;
  }

  /**
   * Update value in the form and refresh invalid rows.
   * @param event Event thrown by the grid.
   */
  private onRowSelected(event: RowSelectedEvent): void {
    const spaces = this.gridApi.getSelectedRows() as Space[];
    this.checkSpaceForm.controls.spaces.setValue(spaces);
    this.checkSpaceForm.controls.spaces.markAsTouched();

    const nodesToRefresh = [event.node];

    // Check if row is invalid and update invalid rows
    if (event.node.isSelected() && (this.hasParentSelected(event.node.data) || this.hasChildrenSelected(event.node.data))) {
      this.invalidRows.push(event.node.data.id);
    } else if (!event.node.isSelected()) {
      this.invalidRows = this.invalidRows.filter(row => {
        const rowNode = this.gridApi.getRowNode(row);
        nodesToRefresh.push(rowNode);
        return row !== event.node.data.id
          && (this.hasParentSelected(rowNode.data) || this.hasChildrenSelected(rowNode.data));
      });
    }

    // Refresh rows
    this.gridApi.redrawRows({
      rowNodes: nodesToRefresh
    });
  }

  /**
   * Checks if a Space has ancestors that are selected in the grid.
   * @param space Space to check.
   * @return true if at least one ancestor is selected, false otherwise.
   * @private
   */
  private hasParentSelected(space: Space): boolean {
    return this.gridApi.getSelectedRows().some(selectedRow =>
      space.parentPath.includes(selectedRow.id)
    );
  }

  /**
   * Checks if a Space has children that are selected in the grid.
   * @param space Space to check.
   * @return true is at least one child is selected, false otherwise.
   * @private
   */
  private hasChildrenSelected(space: Space): boolean {
    return this.gridApi.getSelectedRows().some(selectedRow =>
      selectedRow.parentPath.includes(space.id)
    );
  }
}
